import Animated, {
  interpolate,
  SharedValue,
  useAnimatedStyle,
} from "react-native-reanimated";
import { ColorType, useTheme } from "./AHTheme/AHTheme";
import React, { useMemo } from "react";
import { StyleProp, View, ViewProps, ViewStyle } from "react-native";

import Color from "color";
import { useScaleFactor } from "./AHScaleFactor";

export function useAnimatedPageIndicatorItemStyle({
  size,
  direction,
  index,
}: {
  size: number;
  direction: "vertical" | "horizontal";
  index: number;
}) {
  const sf = useScaleFactor();
  const theme = useTheme();
  const s = Math.ceil(size * sf);
  const style = useMemo(() => {
    return {
      borderRadius: s / 2,
      overflow: "hidden",
      width: s,
      height: s,
      backgroundColor: theme.darkerText,
      marginLeft: sf * (direction !== "vertical" && index !== 0 ? 8 : 0),
      marginTop: sf * (direction === "vertical" && index !== 0 ? 4 : 0),
    } as ViewProps["style"];
  }, [direction, index, s, sf, theme.darkerText]);
  return style;
}

function Dot({
  page,
  index,
  direction,
  size,
  numberOfPages,
}: {
  page: SharedValue<number>;
  numberOfPages: number;
  index: number;
  direction: "horizontal" | "vertical";
  size: number;
}) {
  const style = useAnimatedPageIndicatorItemStyle({ size, direction, index });
  const inputRange = useMemo(
    () =>
      Array(Math.max(numberOfPages + 1, 2))
        .fill(0)
        .map((_, i) => i),
    [numberOfPages]
  );

  const outputRange = useMemo(
    () => inputRange.map((_, i) => (i === index ? 1 : 0.1)),
    [index, inputRange]
  );
  const animatedStyle = useAnimatedStyle(() => ({
    opacity: interpolate(page.value, inputRange, outputRange),
  }));
  return <Animated.View key={index} style={[style, animatedStyle]} />;
}

export function AHAnimatedPageIndicator({
  direction,
  numberOfItems,
  page,
  size = 6,
  backgroudColor,
  LastItemComponent,
}: {
  direction: "horizontal" | "vertical";
  numberOfItems: number;
  page: SharedValue<number>;
  size?: number;
  backgroudColor?: ColorType;
  LastItemComponent?: React.FunctionComponent<{
    index: number;
    page: SharedValue<number>;
  }>;
}) {
  const items = Array(numberOfItems).fill("");
  const theme = useTheme();

  const sf = useScaleFactor();
  const containerStyle = useMemo<StyleProp<ViewStyle>>(
    () => ({
      flexDirection: direction === "vertical" ? "column" : "row",
      justifyContent: "flex-start",
      alignItems: "center",
      backgroundColor: backgroudColor
        ? theme[backgroudColor]
        : Color(theme.lightText).alpha(0.8).toString(),
      borderRadius: size * 2 * sf,
      padding: (size / 1.5) * sf,
      paddingVertical: size * sf,
      alignSelf: "center",
      shadowColor: theme.darkerText,
      shadowOffset: { height: 2, width: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
    }),
    [backgroudColor, direction, sf, size, theme]
  );

  if (numberOfItems < 2) {
    return null;
  }
  return (
    <View style={containerStyle}>
      {items.map((_, index) => {
        if (items.length - 1 === index && LastItemComponent) {
          return <LastItemComponent key={index} index={index} page={page} />;
        }
        return (
          <Dot
            key={index}
            direction={direction}
            page={page}
            index={index}
            numberOfPages={numberOfItems}
            size={size}
          />
        );
      })}
    </View>
  );
}

function NonAnimatedDot({
  direction,
  size,
  active,
  index,
}: {
  active: boolean;
  direction: "horizontal" | "vertical";
  size: number;
  index: number;
}) {
  const theme = useTheme();
  const style = useAnimatedPageIndicatorItemStyle({ size, direction, index });

  return (
    <View
      style={[
        style,
        { backgroundColor: active ? theme.darkerText : theme.washedText },
      ]}
    />
  );
}

export function AHNonAnimtedPageIndicator({
  direction,
  numberOfItems,
  page,
  size = 6,
  backgroudColor,
}: {
  direction: "horizontal" | "vertical";
  numberOfItems: number;
  page: number;
  size?: number;
  backgroudColor?: ColorType;
}) {
  const items = Array(numberOfItems).fill("");
  const theme = useTheme();

  const scaleSize = useScaleFactor() * size;

  const containerStyle = useMemo<StyleProp<ViewStyle>>(
    () => ({
      flexDirection: direction === "vertical" ? "column" : "row",
      justifyContent: "flex-start",
      alignItems: "center",
      backgroundColor: backgroudColor
        ? theme[backgroudColor]
        : Color(theme.lightText).alpha(0.8).toString(),
      borderRadius: scaleSize * 2,
      padding: scaleSize / 1.5,
      paddingVertical: scaleSize,
      alignSelf: "center",
      shadowColor: theme.darkerText,
      shadowRadius: 4,
      shadowOpacity: 0.1,
    }),
    [backgroudColor, direction, scaleSize, theme]
  );
  return (
    <View style={containerStyle}>
      {items.map((_, index) => {
        return (
          <NonAnimatedDot
            key={index}
            direction={direction}
            active={index === page}
            index={index}
            size={size}
          />
        );
      })}
    </View>
  );
}
